import { Flex } from "@chakra-ui/react";
import { Icon, iconName } from "assets/Icon";
import EditableTable from "components/RFQ/EditableTable/EditableTable";
import { FieldForm, Input } from "components/RFQ/Input";
import { useField } from "formik";
import React, { useEffect, useState } from "react";
import { defaultOptions } from "./tableDefaultOptions";
import { CustomModal } from "components/RFQ/Modal/CustomModal";
import { Button } from "newui/components/Button";
import { QUOTE_TYPE_LABEL } from "newui/features/Rfx/constants/rfx-constants";
import { cn } from "utils/helpers";

const quoteTypeOptions = [
	{
		label: QUOTE_TYPE_LABEL.PRELIMINARIES,
		field: { name: QUOTE_TYPE_LABEL.PRELIMINARIES, description: "" },
		placeholder: "Project Prelims",
		value: QUOTE_TYPE_LABEL.PRELIMINARIES,
	},
	{
		label: QUOTE_TYPE_LABEL.TRADEPACKAGE,
		field: { name: QUOTE_TYPE_LABEL.TRADEPACKAGE, description: "" },
		placeholder:
			"Concrete - Supply and Install including repairs and supervision",
		value: QUOTE_TYPE_LABEL.TRADEPACKAGE,
	},
	{
		label: QUOTE_TYPE_LABEL.CONTINGENCY,
		field: { name: QUOTE_TYPE_LABEL.CONTINGENCY, description: "" },
		placeholder: QUOTE_TYPE_LABEL.CONTINGENCY,
		value: QUOTE_TYPE_LABEL.CONTINGENCY,
	},
	{
		label: QUOTE_TYPE_LABEL.PROFESSIONALFEES,
		field: { name: QUOTE_TYPE_LABEL.PROFESSIONALFEES, description: "" },
		placeholder: "Lead Design Architect Fees for Stage 1",
		value: QUOTE_TYPE_LABEL.PROFESSIONALFEES,
	},
	{
		label: QUOTE_TYPE_LABEL.PROVISIONALSUMS,
		field: { name: QUOTE_TYPE_LABEL.PROVISIONALSUMS, description: "" },
		placeholder: QUOTE_TYPE_LABEL.PROVISIONALSUMS,
		value: QUOTE_TYPE_LABEL.PROVISIONALSUMS,
	},
	{
		label: QUOTE_TYPE_LABEL.OTHER,
		field: { name: QUOTE_TYPE_LABEL.OTHER, description: "" },
		placeholder: "Hourly Rates for additional Services",
		value: QUOTE_TYPE_LABEL.OTHER,
	},
];

const ChangeQuoteTypeConfirmDialog = ({
	handleOnQuoteTypeChange,
	setIsModalOpen,
	isModalOpen,
}) => {
	const FooterComponent = () => {
		return (
			<div className="flex w-full justify-between gap-2">
				<Button
					btntype="plain-border"
					onClick={() => setIsModalOpen(false)}
					className="!w-full"
				>
					Cancel
				</Button>
				<Button
					className="flex !w-full gap-3"
					onClick={handleOnQuoteTypeChange}
					data-testid="confirm-change-quote-type"
				>
					Yes
				</Button>
			</div>
		);
	};

	return (
		<CustomModal
			isOpen={isModalOpen}
			onClose={() => setIsModalOpen(false)}
			header={{
				bgColor: "bg-brand-secondary",
				title: "Are you sure you want to change the quote type?",
				description:
					"This action will result in your current quote data being lost.",
			}}
			footerComponent={<FooterComponent />}
		/>
	);
};

export const QuoteContent = ({
	initialDataItem,
	tableVariable,
	index,
	disable,
	isPreview = false,
	type,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [isEditingDesc, setIsEditingDesc] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [changedQuoteData, setChangedQuoteData] = useState(null);
	const [isSectionNameFilled, setIsSectionNameFilled] = useState(false);
	const [isDescriptionFilled, setIsDescriptionFilled] = useState(false);

	const fieldname = (name) => `quote[${index}][${name}]`;
	const [, { error: itemsError, value: items }] = useField(fieldname("items"));
	const [, { value: name }, { setTouched: touchedName, setValue: setName }] =
		useField(fieldname("name"));
	const [
		,
		{ error: descriptionError, value: description },
		{ setTouched: touchedDescription, setValue: setDescription },
	] = useField(fieldname("description"));
	const [isUnitOfMeasurementError, setIsUniteOfMeasurementError] =
		useState(false);
	const data = initialDataItem;

	const [, { value: rowData }, { setValue: setRowData }] = useField(
		fieldname("items")
	);

	const addRow = (name) => {
		setRowData([
			...rowData,
			{
				id: null,
				name: name,
				unit_id: "",
				sort_order: rowData?.length + 1,
				isRemove: 0,
			},
		]);
	};

	const copyRow = (row, index) => {
		setRowData([
			...rowData,
			{
				id: null,
				placeholder: row.placeholder,
				name: null,
				unit_id: row.unit_id,
				sort_order: rowData?.length + 1,
				isRemove: row.isRemove,
			},
		]);
	};

	const removeRow = (row, index) => {
		if (row.id) {
			setRowData(
				rowData.map((item) => {
					if (item.id === row.id) {
						return { ...item, isRemove: 1 };
					}
					return item;
				})
			);
		} else {
			setRowData(rowData.filter((_, i) => index !== i));
		}
	};

	const handleChangeType = (e) => {
		setIsEditing(false);
		setIsEditingDesc(false);
		setName(e.field.name);
		setDescription(e.field.description);

		// set all existing rows (saved to DB) to be removed
		// this ensures we do not get 'ghost' rows appearing against the quote later
		const newData = rowData
			.filter((item) => item.id !== null)
			.map((item) => {
				return { ...item, isRemove: 1 };
			});

		// add default rows for the selection
		newData.push(...defaultOptions(e.field.name));

		setRowData(newData);
		setIsModalOpen(false);
	};

	const handleQuoteChange = (e) => {
		setIsSectionNameFilled(true);
		setIsModalOpen(true);
		setChangedQuoteData(e);
	};

	useEffect(() => {
		if (itemsError) {
			let tempUnitOfMeasurementError = false;
			for (let a = 0; a < itemsError?.length; a++) {
				if (itemsError[a]?.unit_id) {
					setIsUniteOfMeasurementError(true);
					break;
				} else {
					setIsUniteOfMeasurementError(tempUnitOfMeasurementError);
				}
			}
		}
		setIsSectionNameFilled(name !== "");
		setIsDescriptionFilled(description !== "");
	}, [itemsError, name, description]);

	const isSubmission = type === "submission";
	const isCustomName = (name) =>
		!quoteTypeOptions.some((option) => option.value === name) ||
		name === "Other";

	const subTotal = items?.reduce((a, b) => {
		return a + (b?.rate * b?.total || 0);
	}, 0);
	const GST = subTotal * 0.1;
	const includeGST = subTotal + GST;
	const currencyFormat = (number) =>
		(number || 0).toLocaleString("en-US", {
			style: "currency",
			currency: "USD",
		});

	const ErrorMessageComp = ({ text }) => {
		return <div className="text-md mt-2 text-red-500">{text}</div>;
	};

	const getPlaceHolder = (name) => {
		let placeholder = quoteTypeOptions.find(
			(option) => option.label === name
		)?.placeholder;
		if (placeholder) {
			return placeholder;
		} else {
			return "";
		}
	};

	const errorClasses = "border border-red-500 rounded-md p-1";
	const noRowAdded = typeof itemsError === "string";

	return (
		<div>
			<Flex justifyContent={"space-between"}>
				<div
					id="card-header"
					className="w-2/3"
				>
					<div className="flex items-center gap-3">
						{isEditing && !isSubmission ? (
							<FieldForm
								name={fieldname("name")}
								shouldShowError={false}
								classNameBoxInput="bg-black"
								size="sm"
								type="text"
								className={cn(`w-2/4`, !isSectionNameFilled && errorClasses)}
								onKeyUp={(e) => {
									e.target.value.length > 0
										? setIsSectionNameFilled(true)
										: setIsSectionNameFilled(false);
									if (e.key === "Enter") {
										e.preventDefault();
										setIsEditing(false);
										setName(e.target.value);
										touchedName(true, true);
									}
								}}
							/>
						) : (
							<h2
								onClick={() => setIsEditing(!isEditing)}
								className={cn(
									"font-roboto text-lg font-semibold text-primary-900",
									!data?.name && "text-gray-500",
									!isSectionNameFilled && errorClasses
								)}
							>
								{data?.name ? data?.name : "Heading"}
							</h2>
						)}
						{!isSubmission && isCustomName(data.name) && (
							<Icon
								icon={iconName(isEditing ? "check" : "edit")}
								onClick={() => setIsEditing(!isEditing)}
								className="cursor-pointer"
								style={!isEditing ? { width: "14px", height: "14px" } : null}
							/>
						)}
						{!isSectionNameFilled && !isPreview ? (
							<div className="ml-2 text-xs text-red-500">
								Heading is required
							</div>
						) : null}
					</div>

					<div className="flex items-center gap-3">
						{isEditingDesc && !isSubmission ? (
							<FieldForm
								name={fieldname("description")}
								classNameBoxInput="bg-black"
								shouldShowError={false}
								size="sm"
								placeholder={getPlaceHolder(data?.name)}
								className={cn(`w-2/4`, !isDescriptionFilled && errorClasses)}
								onKeyUp={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										setIsEditingDesc(false);
										setDescription(e.target.value);
										touchedDescription(true, true);
									}
								}}
							/>
						) : (
							<p
								onClick={() => setIsEditingDesc(!isEditingDesc)}
								data-testid={`${fieldname("description")}click`}
								className={cn(
									!data?.description && "text-gray-500",
									!isDescriptionFilled && errorClasses
								)}
							>
								{data?.description ? data?.description : "Description"}
							</p>
						)}
						{!isSubmission && (
							<Icon
								icon={iconName(isEditingDesc ? "check" : "edit")}
								onClick={() => setIsEditingDesc(!isEditingDesc)}
								className="cursor-pointer"
								style={
									!isEditingDesc ? { width: "14px", height: "14px" } : null
								}
								data-testid={`${fieldname("submit-desc")}`}
							/>
						)}
						{descriptionError ? (
							<div className="ml-2 text-xs text-red-500">
								Description is required
							</div>
						) : null}
					</div>
					{!noRowAdded && itemsError?.length ? (
						isUnitOfMeasurementError ? (
							<ErrorMessageComp text="Please ensure you've selected a unit of measurement for each row" />
						) : (
							<ErrorMessageComp text="Please ensure you've added a description of scope for each row" />
						)
					) : null}
				</div>
				<div style={{ width: "368px" }}>
					{!isPreview && (
						<Input
							type="select"
							name="section"
							placeholder="Select quote type"
							options={quoteTypeOptions}
							checkIcon={true}
							icon="quote-type"
							onChange={handleQuoteChange}
							value={isCustomName(data.name) ? "Other" : data.name}
							testId={`${fieldname("select")}`}
						/>
					)}
				</div>
			</Flex>
			<div className="w-[calc(100% + 88px)] -mx-[44px] overflow-hidden font-roboto">
				<EditableTable
					data={initialDataItem.items}
					tableVariable={tableVariable}
					index={index}
					isPreview={isPreview}
					type={type}
					disable={disable}
					removeRow={removeRow}
					addRow={addRow}
					copyRow={copyRow}
				>
					<tr>
						<td></td>
						<td></td>
						<td></td>
						<td></td>
						<td className="border border-gray-300 bg-bg-gray-200 px-4 py-3 text-xs font-medium text-tertiary-600">
							Subtotal
						</td>
						<td className="border border-gray-300 px-4 py-3">
							{isSubmission ? currencyFormat(subTotal) : "$0.00"}
						</td>
					</tr>
					<tr>
						<td></td>
						<td></td>
						<td></td>
						<td></td>
						<td className="border border-gray-300 bg-bg-gray-200 px-4 py-3 text-xs font-medium text-tertiary-600">
							GST (10%)
						</td>
						<td className="border border-gray-300 px-4 py-3">
							{isSubmission ? currencyFormat(GST) : "$0.00"}
						</td>
					</tr>
					<tr>
						<td></td>
						<td></td>
						<td></td>
						<td></td>
						<td className="border border-gray-300 bg-bg-gray-200 px-4 py-3 text-xs font-medium text-tertiary-600">
							Total Cost
						</td>
						<td className="border border-gray-300 px-4 py-3">
							{isSubmission ? currencyFormat(includeGST) : "$0.00"}
						</td>
					</tr>
				</EditableTable>
			</div>
			{noRowAdded && (
				<ErrorMessageComp text="Please ensure you've added at least one row" />
			)}
			<ChangeQuoteTypeConfirmDialog
				isModalOpen={isModalOpen}
				handleOnQuoteTypeChange={() => handleChangeType(changedQuoteData)}
				setIsModalOpen={setIsModalOpen}
			/>
		</div>
	);
};
